import React, { useMemo } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import {
  CheckCircle as ResolvesIcon,
  WarningAmber as MissingIcon,
  TextFields as LiteralIcon,
} from '@mui/icons-material';
import {
  extractJsonPaths,
  resolveJsonPath,
  looksLikePath,
  previewValue,
  type JsonPathEntry,
} from '../../../../shared/utils/jsonPath';

interface FieldMappingInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  sampleEvent: Record<string, unknown>;
  multiline?: boolean;
  disabled?: boolean;
  /** Shown under an empty input: what happens when this field has no mapping. */
  emptyText?: string;
  error?: string;
  /** Paths the reader of this field accepts: the dropdown offers no other sample path. */
  pathFilter?: (path: string) => boolean;
}

/**
 * One field-mapping input driven by the rule's sample event. The dropdown lists
 * every path present in the sample (with its value); the line below shows what
 * the current entry resolves to — or warns when a path isn't in the sample.
 */
const FieldMappingInput: React.FC<FieldMappingInputProps> = ({
  label,
  value,
  onChange,
  sampleEvent,
  multiline,
  disabled,
  emptyText,
  error,
  pathFilter,
}) => {
  // The sample walk would otherwise rerun on every keystroke; the paths change only with the sample or filter.
  const options = useMemo(
    () => extractJsonPaths(sampleEvent).filter((o) => !pathFilter || pathFilter(o.path)),
    [sampleEvent, pathFilter],
  );

  const preview = useMemo(() => {
    const trimmed = value.trim();
    if (!trimmed) {
      return { kind: 'empty' as const, text: emptyText ?? '' };
    }
    if (looksLikePath(trimmed)) {
      const resolved = resolveJsonPath(trimmed, sampleEvent);
      if (resolved === undefined) {
        return { kind: 'missing' as const, text: 'Not in sample event' };
      }
      return { kind: 'resolved' as const, text: previewValue(resolved) };
    }
    return { kind: 'literal' as const, text: trimmed };
  }, [value, sampleEvent, emptyText]);

  return (
    <Box>
      <Autocomplete<JsonPathEntry, false, false, true>
        freeSolo
        openOnFocus
        disabled={disabled}
        options={options}
        inputValue={value}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.path)}
        onInputChange={(_, v, reason) => {
          if (reason === 'input' || reason === 'clear') onChange(v);
        }}
        onChange={(_, selected) => {
          if (selected && typeof selected !== 'string') onChange(selected.path);
        }}
        filterOptions={(opts, state) => {
          const q = state.inputValue.trim().toLowerCase();
          if (!q) return opts;
          return opts.filter(
            (o) =>
              o.path.toLowerCase().includes(q) ||
              previewValue(o.value).toLowerCase().includes(q),
          );
        }}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', flexShrink: 0 }}>
              {option.path}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, textAlign: 'right' }}>
              {previewValue(option.value, 40)}
            </Typography>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={label}
            placeholder="Field or fixed text"
            error={!!error}
            multiline={multiline}
            minRows={multiline ? 2 : undefined}
            InputLabelProps={{ shrink: true }}
            InputProps={{ ...params.InputProps, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
          />
        )}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, minHeight: 18, px: 0.5 }}>
        {error && (
          <Typography variant="caption" color="error" sx={{ flex: 1 }}>
            {error}
          </Typography>
        )}
        {!error && preview.kind === 'resolved' && (
          <>
            <ResolvesIcon sx={{ fontSize: 13, color: 'success.main' }} />
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
              → {preview.text}
            </Typography>
          </>
        )}
        {!error && preview.kind === 'missing' && (
          <>
            <MissingIcon sx={{ fontSize: 13, color: 'warning.main' }} />
            <Typography variant="caption" color="warning.main" noWrap sx={{ flex: 1 }}>
              {preview.text}
            </Typography>
          </>
        )}
        {!error && preview.kind === 'literal' && (
          <>
            <LiteralIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
              Fixed text
            </Typography>
          </>
        )}
        {!error && preview.kind === 'empty' && (
          <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
            {preview.text}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default FieldMappingInput;
